import React, { useRef, useState, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import './MediaPlayer.css';

function formatTime(seconds) {
  if (isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function isHlsUrl(url) {
  return url && (url.includes('.m3u8') || url.includes('.m3u'));
}

function MediaPlayer({ src, poster, title }) {
  const videoRef     = useRef(null);
  const progressRef  = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimer = useRef(null);
  const hlsRef       = useRef(null);

  const [playing,      setPlaying]      = useState(false);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [duration,     setDuration]     = useState(0);
  const [volume,       setVolume]       = useState(1);
  const [muted,        setMuted]        = useState(false);
  const [fullscreen,   setFullscreen]   = useState(false);
  const [buffered,     setBuffered]     = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [loading,      setLoading]      = useState(false);
  const [streamError,  setStreamError]  = useState(null);
  const [quality]                       = useState('HD');

  // ── HLS / source initialisation ────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset on src change
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setBuffered(0);
    setStreamError(null);
    setLoading(false);

    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    video.removeAttribute('src');
    video.load();

    if (!src) return;

    // Auto-play when a new stream is selected.
    // Falls back to muted playback if the browser blocks unmuted autoplay.
    const tryAutoplay = () => {
      video.play()
        .then(() => setPlaying(true))
        .catch(() => {
          video.muted = true;
          setMuted(true);
          video.play().then(() => setPlaying(true)).catch(() => {});
        });
    };

    let canPlayListener = null;

    if (isHlsUrl(src)) {
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
          tryAutoplay();
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            setStreamError('스트림을 불러올 수 없습니다. 오프라인이거나 차단되었을 수 있습니다.');
            setLoading(false);
          }
        });
        setLoading(true);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src; // Safari native HLS
        canPlayListener = () => tryAutoplay();
        video.addEventListener('canplay', canPlayListener, { once: true });
      } else {
        setStreamError('이 브라우저에서는 HLS 재생을 지원하지 않습니다.');
      }
    } else {
      video.src = src;
      canPlayListener = () => tryAutoplay();
      video.addEventListener('canplay', canPlayListener, { once: true });
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      if (canPlayListener) video.removeEventListener('canplay', canPlayListener);
      video.removeAttribute('src');
      video.load();
    };
  }, [src]);

  // ── Auto-hide controls ─────────────────────────────────────────────────────
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideControlsTimer.current);
    if (playing) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [playing]);

  useEffect(() => {
    return () => clearTimeout(hideControlsTimer.current);
  }, []);

  useEffect(() => {
    if (!playing) {
      setShowControls(true);
      clearTimeout(hideControlsTimer.current);
    }
  }, [playing]);

  // ── Video event handlers ───────────────────────────────────────────────────
  const handleTimeUpdate = () => {
    const vid = videoRef.current;
    if (!vid) return;
    setCurrentTime(vid.currentTime);
    if (vid.buffered.length > 0) {
      setBuffered((vid.buffered.end(vid.buffered.length - 1) / vid.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => { if (videoRef.current) setDuration(videoRef.current.duration); };
  const handleWaiting  = () => setLoading(true);
  const handleCanPlay  = () => setLoading(false);
  const handleEnded    = () => setPlaying(false);
  const handleVideoError = () => {
    setStreamError('스트림을 불러올 수 없습니다. 오프라인이거나 차단되었을 수 있습니다.');
    setLoading(false);
  };

  // ── Controls ───────────────────────────────────────────────────────────────
  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid || streamError) return;
    if (vid.paused) { vid.play(); setPlaying(true); }
    else            { vid.pause(); setPlaying(false); }
    resetHideTimer();
  };

  const seek = (e) => {
    if (!duration) return;
    const rect  = progressRef.current.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    videoRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    videoRef.current.volume = val;
    setVolume(val);
    setMuted(val === 0);
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el.requestFullscreen && el.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen && document.exitFullscreen();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={['player', fullscreen ? 'player--fullscreen' : '', showControls ? 'player--controls-visible' : ''].join(' ')}
      ref={containerRef}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      {/* Video element — src set programmatically via useEffect */}
      <video
        ref={videoRef}
        className="player__video"
        poster={poster}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onWaiting={handleWaiting}
        onCanPlay={handleCanPlay}
        onEnded={handleEnded}
        onError={handleVideoError}
        onClick={togglePlay}
        preload="metadata"
        playsInline
      />

      {/* Error overlay */}
      {streamError && (
        <div className="player__error-overlay">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p>{streamError}</p>
        </div>
      )}

      {/* Loading spinner */}
      {loading && !streamError && (
        <div className="player__spinner-wrap">
          <div className="player__spinner" />
        </div>
      )}

      {/* Big play button overlay (shown when paused & no error) */}
      {!playing && !loading && !streamError && src && (
        <button className="player__big-play" onClick={togglePlay} aria-label="Play">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
      )}

      {/* Controls bar */}
      <div className="player__controls">
        {/* Title bar (top) */}
        {title && (
          <div className="player__title-bar">
            <span>{title}</span>
          </div>
        )}

        {/* Progress bar */}
        <div className="player__progress-wrap">
          <div
            className="player__progress"
            ref={progressRef}
            onClick={seek}
            role="slider"
            aria-label="Seek"
            aria-valuenow={Math.round(progressPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
          >
            <div className="player__progress-buffered" style={{ width: `${buffered}%` }} />
            <div className="player__progress-played" style={{ width: `${progressPercent}%` }}>
              <div className="player__progress-thumb" />
            </div>
          </div>
        </div>

        <div className="player__controls-row">
          {/* Left group */}
          <div className="player__controls-left">
            <button className="player__btn" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6zm8-14v14h4V5z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Volume */}
            <div className="player__volume-group">
              <button className="player__btn" onClick={toggleMute} aria-label="Toggle mute">
                {muted || volume === 0 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                  </svg>
                ) : volume < 0.5 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>
              <input
                className="player__volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
              />
            </div>

            {/* Time */}
            <span className="player__time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Right group */}
          <div className="player__controls-right">
            <span className="player__quality">{quality}</span>
            <button className="player__btn" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
              {fullscreen ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 16h3v3h2v-5H5zm3-8H5v2h5V5H8zm6 11h2v-3h3v-2h-5zm2-11V5h-2v5h5V8z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7zm-2-4h2V7h3V5H5zm12 7h-3v2h5v-5h-2zM14 5v2h3v3h2V5z" />
                </svg>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default MediaPlayer;
